import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ConnectionStatus, InitialLoadState, NotificationResponse } from './types';

interface NotificationsState {
  restaurantId: string | null;
  items: NotificationResponse[];
  unreadCount: number;
  nextCursor: string | null;
  hasMore: boolean;
  connectionStatus: ConnectionStatus;
  initialLoadState: InitialLoadState;
  isInitialFlush: boolean;
  /** True once the user has granted autoplay by clicking "Enable sound". Global (browser-level). */
  soundUnlocked: boolean;
  /** Transient — order whose row/card should flash due to a realtime event. Cleared after HIGHLIGHT_TTL_MS. */
  highlightedOrderId: string | null;
  /** Order IDs that received a T-90min reminder SSE and have not yet been acted on. Not persisted. */
  reminderOrders90: Set<string>;
  /** Order IDs that received a T-60min reminder SSE and have not yet been acted on. Not persisted. */
  reminderOrders60: Set<string>;

  bind: (restaurantId: string) => void;
  unbind: () => void;
  setConnectionStatus: (s: ConnectionStatus) => void;
  setInitialLoadState: (s: InitialLoadState) => void;
  setInitialFlush: (v: boolean) => void;
  /**
   * Seed state from a paginated list response. Replaces the current page
   * (used on route entry + after reconnect).
   */
  seedList: (payload: {
    items: NotificationResponse[];
    unreadCount: number;
    nextCursor: string | null;
    hasMore: boolean;
  }) => void;
  appendPage: (payload: {
    items: NotificationResponse[];
    nextCursor: string | null;
    hasMore: boolean;
  }) => void;
  /**
   * Insert a notification arriving via SSE. During the initial-flush window
   * items are sorted by createdAt; afterwards they are prepended.
   */
  receive: (n: NotificationResponse) => void;
  markOneRead: (notificationId: string) => void;
  markAllReadLocal: () => void;
  removeOne: (notificationId: string) => void;
  clearAll: () => void;
  unlockSound: () => void;
  highlightOrder: (orderId: string) => void;
  addReminder90: (orderId: string) => void;
  addReminder60: (orderId: string) => void;
  clearReminder: (orderId: string) => void;
}

const HIGHLIGHT_TTL_MS = 2500;

function insertSorted(list: NotificationResponse[], n: NotificationResponse): NotificationResponse[] {
  const exists = list.findIndex((x) => x.notificationId === n.notificationId);
  if (exists !== -1) {
    const copy = list.slice();
    copy[exists] = n;
    return copy;
  }
  const t = Date.parse(n.createdAt);
  const out: NotificationResponse[] = [];
  let inserted = false;
  for (const item of list) {
    if (!inserted && Date.parse(item.createdAt) < t) {
      out.push(n);
      inserted = true;
    }
    out.push(item);
  }
  if (!inserted) out.push(n);
  return out;
}

export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set, get) => ({
      restaurantId: null,
      items: [],
      unreadCount: 0,
      nextCursor: null,
      hasMore: false,
      connectionStatus: 'idle',
      initialLoadState: 'idle',
      isInitialFlush: false,
      soundUnlocked: false,
      highlightedOrderId: null,
      reminderOrders90: new Set(),
      reminderOrders60: new Set(),

      bind: (restaurantId) => {
        if (get().restaurantId === restaurantId) return;
        set({
          restaurantId,
          items: [],
          unreadCount: 0,
          nextCursor: null,
          hasMore: false,
          connectionStatus: 'idle',
          initialLoadState: 'idle',
          isInitialFlush: false,
          highlightedOrderId: null,
          reminderOrders90: new Set(),
          reminderOrders60: new Set(),
        });
      },

      unbind: () => {
        set({
          restaurantId: null,
          items: [],
          unreadCount: 0,
          nextCursor: null,
          hasMore: false,
          connectionStatus: 'idle',
          initialLoadState: 'idle',
          isInitialFlush: false,
          highlightedOrderId: null,
          reminderOrders90: new Set(),
          reminderOrders60: new Set(),
        });
      },

      setConnectionStatus: (connectionStatus) => set({ connectionStatus }),
      setInitialLoadState: (initialLoadState) => set({ initialLoadState }),
      setInitialFlush: (isInitialFlush) => set({ isInitialFlush }),

      seedList: ({ items, unreadCount, nextCursor, hasMore }) => {
        set({ items, unreadCount, nextCursor, hasMore });
      },

      appendPage: ({ items, nextCursor, hasMore }) => {
        set((state) => {
          const seen = new Set(state.items.map((x) => x.notificationId));
          const merged = state.items.concat(items.filter((x) => !seen.has(x.notificationId)));
          return { items: merged, nextCursor, hasMore };
        });
      },

      receive: (n) => {
        set((state) => {
          const existing = state.items.find((x) => x.notificationId === n.notificationId);
          const nextItems = state.isInitialFlush
            ? insertSorted(state.items, n)
            : existing
              ? state.items.map((x) => (x.notificationId === n.notificationId ? n : x))
              : [n, ...state.items];
          const incUnread = !existing && !n.isRead ? 1 : 0;
          return { items: nextItems, unreadCount: state.unreadCount + incUnread };
        });
      },

      markOneRead: (notificationId) => {
        set((state) => {
          const now = new Date().toISOString();
          let decremented = false;
          const items = state.items.map((x) => {
            if (x.notificationId !== notificationId) return x;
            if (!x.isRead) decremented = true;
            return { ...x, isRead: true, readAt: x.readAt ?? now };
          });
          return {
            items,
            unreadCount: decremented ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          };
        });
      },

      markAllReadLocal: () => {
        set((state) => {
          const now = new Date().toISOString();
          return {
            items: state.items.map((x) =>
              x.isRead ? x : { ...x, isRead: true, readAt: x.readAt ?? now }
            ),
            unreadCount: 0,
          };
        });
      },

      removeOne: (notificationId) => {
        set((state) => {
          const target = state.items.find((x) => x.notificationId === notificationId);
          const lostUnread = target && !target.isRead ? 1 : 0;
          return {
            items: state.items.filter((x) => x.notificationId !== notificationId),
            unreadCount: Math.max(0, state.unreadCount - lostUnread),
          };
        });
      },

      clearAll: () => {
        set({ items: [], unreadCount: 0, nextCursor: null, hasMore: false });
      },

      unlockSound: () => set({ soundUnlocked: true }),

      addReminder90: (orderId) =>
        set((state) => ({
          reminderOrders90: new Set([...state.reminderOrders90, orderId]),
        })),

      addReminder60: (orderId) =>
        set((state) => ({
          reminderOrders60: new Set([...state.reminderOrders60, orderId]),
          reminderOrders90: new Set([...state.reminderOrders90, orderId]),
        })),

      clearReminder: (orderId) =>
        set((state) => {
          const r90 = new Set(state.reminderOrders90);
          const r60 = new Set(state.reminderOrders60);
          r90.delete(orderId);
          r60.delete(orderId);
          return { reminderOrders90: r90, reminderOrders60: r60 };
        }),

      highlightOrder: (orderId) => {
        set({ highlightedOrderId: orderId });
        setTimeout(() => {
          const current = useNotificationsStore.getState().highlightedOrderId;
          if (current === orderId) set({ highlightedOrderId: null });
        }, HIGHLIGHT_TTL_MS);
      },
    }),
    {
      name: 'restaurant-hub-notifications-prefs',
      storage: createJSONStorage(() => localStorage),
      version: 2,
      // v0/v1 stored a soundMuted preference. v2 drops it — notification volume is no
      // longer user-configurable. Stale soundMuted entries in localStorage are ignored.
      migrate: (persistedState, version) => {
        if (version < 2 && persistedState && typeof persistedState === 'object') {
          const old = persistedState as { soundUnlocked?: unknown };
          return {
            soundUnlocked: Boolean(old.soundUnlocked ?? false),
          };
        }
        return persistedState as Partial<NotificationsState>;
      },
      // Only persist user preferences — live notification data is re-seeded per session.
      partialize: (state) => ({
        soundUnlocked: state.soundUnlocked,
      }),
    }
  )
);
